import { useMemo } from 'react';
import {
  AuthenticatedGuard,
  CommunityNewAssetsPage,
  CommunityNewFeedPage,
  PageActivity,
  PageBookmarks,
  PageCalendar,
  PageChannels,
  PageChannelsCommunities,
  PageChannelsEvents,
  PageChannelsGivings,
  PageChannelsPrograms,
  PageChannelsVideos,
  PageChatDetail,
  PageChatrooms,
  PageCommunityAssets,
  PageCommunityFeed,
  PageCommunityMembers,
  PageConsents,
  PageCreateChat,
  PageCustomChannelsDetail,
  PageEventDetail,
  PageFeed,
  PageGivingsList,
  PageGlobalSearch,
  PageGroupDetailAbout,
  PageGroupDetailAnnouncements,
  PageGroupsList,
  PageHelp,
  PageJoinedCommunities,
  PageJoinedOrganizations,
  PageJoinedPrograms,
  PageJournal,
  PageMemberCommunities,
  PageMemberMessage,
  PageMemberOrganizations,
  PageMemberProfile,
  PageMemberPrograms,
  PageNotifications,
  PageOnboardingTour,
  PageOrganizationDetail,
  PageOrganizationsCommunities,
  PageOrganizationsGivings,
  PageOrganizationsPrograms,
  PageOrganizationsVideos,
  PagePoll,
  PageProgramAbout,
  PageProgramAssets,
  PageProgramMembers,
  PageProgramTimeline,
  PageVideoDetail,
  PageVideos,
  PageWithingsOrderPost,
  PreconditionFailedGuard,
  TimezoneConfirmationGuard,
} from 'pages';
import { GivingScopeEnum } from 'features/givings';
import { PageSettings } from 'pages/page-settings';
import { NavigateWithSearch } from '../../navigate-with-search';
import { NavigateWithUserInfo } from '../../navigate-with-user-info';
import { routes } from '../../routes';
import { PermissionGuard } from '../permission-guard';

export const useAuthenticatedWebRouter = () =>
  useMemo(
    () => [
      {
        children: [
          {
            children: [
              {
                children: [
                  {
                    element: <PageOnboardingTour />,
                    path: routes.onboardingTour.index,
                  },
                  {
                    element: <PageConsents />,
                    path: routes.consents,
                  },
                  {
                    element: (
                      <NavigateWithSearch additionalSearch={{ scope: 'dexcom-sync' }} to={routes.settings.index} />
                    ),
                    path: routes.settings.dexcomAccountSync,
                  },
                  {
                    element: <PageSettings />,
                    path: routes.settings.index,
                  },
                  {
                    element: <PageNotifications />,
                    path: '/notifications',
                  },
                  {
                    element: <PageHelp />,
                    path: '/help',
                  },
                  {
                    element: <PageProgramAbout />,
                    path: routes.program.about(':programId'),
                  },
                  {
                    element: <PageProgramMembers />,
                    path: routes.program.members(':programId'),
                  },
                  {
                    element: <PageProgramTimeline />,
                    path: routes.program.timeline(':programId'),
                  },
                  {
                    element: <PageProgramAssets />,
                    path: routes.program.assets(':programId'),
                  },
                  {
                    element: <PageChatrooms />,
                    path: routes.chat.index,
                  },
                  {
                    element: <PageCreateChat />,
                    path: routes.chat.create,
                  },
                  {
                    element: <PageChatDetail />,
                    path: routes.chat.chatroom(':chatId'),
                  },
                  {
                    element: <PageMemberMessage />,
                    path: routes.members.message(':memberId'),
                  },
                  {
                    element: <NavigateWithUserInfo />,
                    path: routes.user.profile,
                  },
                  {
                    element: <NavigateWithUserInfo />,
                    path: routes.profile.personalInfo,
                  },
                  { element: <NavigateWithSearch to={routes.settings.index} />, path: routes.profile.language },
                  { element: <NavigateWithSearch to={routes.settings.index} />, path: routes.profile.changePassword },
                  { element: <NavigateWithSearch to={routes.settings.index} />, path: routes.profile.removeAccount },
                  {
                    element: <NavigateWithSearch to={routes.settings.index} />,
                    path: routes.profile.settings.notifications,
                  },
                  {
                    element: <NavigateWithSearch to={routes.settings.index} />,
                    path: routes.profile.settings.translations,
                  },
                  {
                    element: <PageMemberProfile />,
                    path: routes.members.detail(':memberId'),
                  },
                  {
                    element: <PageMemberCommunities />,
                    path: routes.members.entities(':memberId', 'communities'),
                  },
                  {
                    element: <PageMemberPrograms />,
                    path: routes.members.entities(':memberId', 'programs'),
                  },
                  {
                    element: <PageMemberOrganizations />,
                    path: routes.members.entities(':memberId', 'organizations'),
                  },
                  {
                    element: <PageCalendar />,
                    path: routes.calendar.index,
                  },
                  {
                    element: <PageEventDetail />,
                    path: routes.events.comments(':eventId', ':commentId?', ':replyId?'),
                  },
                  {
                    element: <NavigateWithSearch to={routes.calendar.index} />,
                    path: routes.events.index,
                  },

                  {
                    element: (
                      <NavigateWithSearch<{ eventId: string }> to={(params) => routes.events.detail(params.eventId)} />
                    ),
                    path: routes.events.attendeesRedirect(':eventId'),
                  },
                  {
                    element: (
                      <NavigateWithSearch<{ campaignId: string }>
                        to={(params) => routes.giving.detail(params.campaignId)}
                      />
                    ),
                    path: routes.campaign.detail(':organizationId', ':campaignId'),
                  },
                  {
                    element: <NavigateWithSearch to={routes.giving.browse} />,
                    path: routes.giving.index,
                  },
                  {
                    element: <PageGivingsList scope={GivingScopeEnum.BROWSE} />,
                    path: routes.giving.browse,
                  },
                  {
                    element: <PageGivingsList scope={GivingScopeEnum.NOW} />,
                    path: routes.giving.now,
                  },
                  {
                    element: <PageCommunityAssets />,
                    path: '/communities/:communityId/assets',
                  },
                  {
                    element: <CommunityNewAssetsPage />,
                    path: routes.community.new.assets(':communityId'),
                  },
                  {
                    element: <PageCommunityMembers />,
                    path: '/communities/:communityId/members',
                  },
                  {
                    element: <PageCommunityFeed />,
                    path: routes.community.feed(':communityId'),
                  },
                  {
                    element: <CommunityNewFeedPage />,
                    path: routes.community.new.feed(':communityId'),
                  },
                  {
                    element: <PageFeed />,
                    path: '/feed',
                  },
                  {
                    element: <PageGlobalSearch />,
                    path: '/search/:scope',
                  },
                  {
                    element: <NavigateWithSearch to="/feed" />,
                    path: '/',
                  },
                  {
                    element: <PageBookmarks />,
                    path: '/bookmarks',
                  },
                  {
                    element: <PageJournal />,
                    path: '/journal',
                  },
                  {
                    element: <PagePoll />,
                    path: '/polls/:postId/comments?/:commentId?/:replyId?',
                  },
                  {
                    element: <PageActivity />,
                    path: '/activities/:postId/comments?/:commentId?/:replyId?',
                  },
                  {
                    element: <PageWithingsOrderPost />,
                    path: '/withings_order_posts/:orderPostId/comments?/:commentId?/:replyId?',
                  },
                  {
                    element: <NavigateWithSearch to={routes.community.index} />,
                    path: routes.community.indexRedirect,
                  },
                  {
                    element: <PageJoinedCommunities />,
                    path: routes.community.index,
                  },
                  {
                    element: <NavigateWithSearch to={routes.program.index} />,
                    path: routes.program.indexRedirect,
                  },
                  {
                    element: <PageJoinedPrograms />,
                    path: routes.program.index,
                  },
                  {
                    element: (
                      <NavigateWithSearch<{ programId: string }>
                        to={(params) => routes.program.timeline(params.programId)}
                      />
                    ),
                    path: routes.program.detailDeprecated(':programId'),
                  },
                  {
                    element: (
                      <NavigateWithSearch<{ programId: string }>
                        to={(params) => routes.program.timeline(params.programId)}
                      />
                    ),
                    path: routes.program.detail(':programId'),
                  },
                  {
                    element: <PageJoinedOrganizations />,
                    path: routes.organization.index,
                  },
                  {
                    element: (
                      <NavigateWithSearch<{ organizationId: string }>
                        to={(params) => routes.organization.detail(params.organizationId)}
                      />
                    ),
                    path: routes.organization.about(':organizationId'),
                  },
                  {
                    element: <PageOrganizationDetail />,
                    path: routes.organization.detail(':organizationId'),
                  },
                  {
                    element: <PageGroupDetailAbout />,
                    path: routes.group.detail.scope(':groupId', 'about'),
                  },
                  {
                    element: <PageGroupDetailAnnouncements />,
                    path: routes.group.detail.scope(':groupId', 'announcements'),
                  },
                  {
                    element: <PageGroupsList />,
                    path: routes.group.entities(':groupId', ':scope'),
                  },
                  {
                    element: (
                      <NavigateWithSearch<{ groupId: string }>
                        to={(params) => routes.group.detail.scope(params.groupId, 'announcements')}
                      />
                    ),
                    path: routes.group.detail.index(':groupId'),
                  },
                  {
                    element: <PageVideoDetail />,
                    path: routes.video.detail(':videoId'),
                  },
                  {
                    children: [
                      {
                        element: <PageVideos />,
                        path: routes.video.index,
                      },
                    ],
                    element: <PermissionGuard permissions={[{ scope: 'can-view-videos' }]} />,
                  },
                  {
                    element: <PageOrganizationsGivings />,
                    path: routes.organization.entities(':organizationId', 'givings'),
                  },
                  {
                    element: <PageOrganizationsCommunities />,
                    path: routes.organization.entities(':organizationId', 'communities'),
                  },
                  {
                    element: <PageOrganizationsPrograms />,
                    path: routes.organization.entities(':organizationId', 'programs'),
                  },
                  {
                    children: [
                      {
                        element: <PageOrganizationsVideos />,
                        path: routes.organization.entities(':organizationId', 'videos'),
                      },
                    ],
                    element: <PermissionGuard permissions={[{ scope: 'can-view-videos' }]} />,
                  },
                  {
                    element: <PageChannelsCommunities />,
                    path: routes.channels.organizationChannel(':organizationId', 'communities'),
                  },
                  {
                    element: <PageChannelsPrograms />,
                    path: routes.channels.organizationChannel(':organizationId', 'programs'),
                  },
                  {
                    element: <PageChannelsEvents />,
                    path: routes.channels.organizationChannel(':organizationId', 'events'),
                  },
                  {
                    element: <PageChannelsGivings />,
                    path: routes.channels.organizationChannel(':organizationId', 'givings'),
                  },
                  {
                    element: <PageChannelsVideos />,
                    path: routes.channels.organizationChannel(':organizationId', 'videos'),
                  },
                  {
                    element: <PageCustomChannelsDetail />,
                    path: routes.channels.customChannelDetail(':channelId'),
                  },
                  {
                    element: <PageChannels />,
                    path: '/channels',
                  },
                ],
                element: <TimezoneConfirmationGuard />,
              },
            ],
            element: <PreconditionFailedGuard />,
          },
        ],
        element: <AuthenticatedGuard />,
      },
    ],
    [],
  );
